import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { useEmailLogs, EmailLog } from "@/hooks/useEmailLogs";
import { EmailLogDetailsDialog } from "@/components/EmailLogDetailsDialog";
import { RetryEmailDialog, isRetryable } from "@/components/RetryEmailDialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ArrowLeft, RefreshCw, Search, Eye, CheckCircle2, XCircle, RotateCw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

const getEmailTypeBadge = (type: string) => {
  const variants: Record<string, { label: string; className: string }> = {
    confirmation: { label: 'Confirmation', className: 'bg-blue-500/10 text-blue-500 hover:bg-blue-500/20' },
    modification: { label: 'Modification', className: 'bg-orange-500/10 text-orange-500 hover:bg-orange-500/20' },
    cancellation: { label: 'Annulation', className: 'bg-red-500/10 text-red-500 hover:bg-red-500/20' },
    reminder: { label: 'Rappel', className: 'bg-purple-500/10 text-purple-500 hover:bg-purple-500/20' },
    followup: { label: 'Suivi', className: 'bg-yellow-500/10 text-yellow-500 hover:bg-yellow-500/20' },
  };
  return variants[type] || { label: type, className: '' };
};

const getStatusBadge = (status: string) => {
  if (status === 'sent') {
    return { icon: CheckCircle2, label: 'Envoyé', className: 'bg-green-500/10 text-green-500' };
  }
  return { icon: XCircle, label: 'Échoué', className: 'bg-red-500/10 text-red-500' };
};

export default function EmailLogs() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [emailType, setEmailType] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [search, setSearch] = useState('');
  const [selectedLog, setSelectedLog] = useState<EmailLog | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [retryLog, setRetryLog] = useState<EmailLog | null>(null);
  const [retryOpen, setRetryOpen] = useState(false);

  const { data, isLoading, refetch } = useEmailLogs({
    page,
    limit: 50,
    emailType: emailType && emailType !== 'all' ? emailType : undefined,
    status: status && status !== 'all' ? status : undefined,
    search: search || undefined,
  });

  const handleViewDetails = (log: EmailLog) => {
    setSelectedLog(log);
    setDetailsOpen(true);
  };

  return (
    <div className="min-h-screen bg-background p-4 md:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="icon"
              onClick={() => navigate('/dashboard')}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold">Emails envoyés</h1>
              <p className="text-muted-foreground">Historique de tous les emails envoyés</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle>Filtres</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Rechercher par nom ou email..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={emailType} onValueChange={setEmailType}>
                <SelectTrigger>
                  <SelectValue placeholder="Type d'email" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les types</SelectItem>
                  <SelectItem value="confirmation">Confirmation</SelectItem>
                  <SelectItem value="modification">Modification</SelectItem>
                  <SelectItem value="cancellation">Annulation</SelectItem>
                  <SelectItem value="reminder">Rappel</SelectItem>
                  <SelectItem value="followup">Suivi</SelectItem>
                </SelectContent>
              </Select>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="sent">Envoyé</SelectItem>
                  <SelectItem value="failed">Échoué</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                onClick={() => {
                  setSearch('');
                  setEmailType('all');
                  setStatus('all');
                  setPage(1);
                }}
              >
                Réinitialiser
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>
                {data?.total || 0} email{(data?.total || 0) > 1 ? 's' : ''}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center py-8">
                <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date/Heure</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead>Réservation</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data?.logs.map((log) => {
                      const emailTypeBadge = getEmailTypeBadge(log.email_type);
                      const statusBadge = getStatusBadge(log.status);
                      const StatusIcon = statusBadge.icon;

                      return (
                        <TableRow key={log.id}>
                          <TableCell className="font-mono text-sm">
                            {format(new Date(log.sent_at), 'dd/MM/yyyy HH:mm', { locale: fr })}
                          </TableCell>
                          <TableCell>
                            <Badge className={emailTypeBadge.className}>
                              {emailTypeBadge.label}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              <p className="font-medium">{log.customer_name}</p>
                              <p className="text-xs text-muted-foreground">{log.customer_email}</p>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">
                            {format(new Date(log.reservation_date), 'dd/MM/yyyy', { locale: fr })} <br />
                            <span className="text-muted-foreground">{log.reservation_time.substring(0, 5)}</span>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Badge className={statusBadge.className}>
                                <StatusIcon className="w-3 h-3 mr-1" />
                                {statusBadge.label}
                              </Badge>
                              {log.status === 'failed' && (
                                isRetryable(log) ? (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-7 px-2"
                                    onClick={() => {
                                      setRetryLog(log);
                                      setRetryOpen(true);
                                    }}
                                  >
                                    <RotateCw className="h-3 w-3 mr-1" />
                                    Réessayer
                                  </Button>
                                ) : (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <span>
                                          <Button variant="outline" size="sm" className="h-7 px-2" disabled>
                                            <RotateCw className="h-3 w-3 mr-1" />
                                            Réessayer
                                          </Button>
                                        </span>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        Ce type d'email ne peut pas être renvoyé manuellement.
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                )
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewDetails(log)}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              Voir
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {(!data?.logs || data.logs.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                          Aucun email trouvé
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>

                {/* Pagination */}
                {data && data.totalPages > 1 && (
                  <div className="mt-4">
                    <Pagination>
                      <PaginationContent>
                        <PaginationItem>
                          <PaginationPrevious
                            onClick={() => setPage(Math.max(1, page - 1))}
                            className={page === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                          />
                        </PaginationItem>
                        {Array.from({ length: Math.min(5, data.totalPages) }, (_, i) => {
                          const pageNum = i + 1;
                          return (
                            <PaginationItem key={pageNum}>
                              <PaginationLink
                                onClick={() => setPage(pageNum)}
                                isActive={page === pageNum}
                                className="cursor-pointer"
                              >
                                {pageNum}
                              </PaginationLink>
                            </PaginationItem>
                          );
                        })}
                        <PaginationItem>
                          <PaginationNext
                            onClick={() => setPage(Math.min(data.totalPages, page + 1))}
                            className={page === data.totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                          />
                        </PaginationItem>
                      </PaginationContent>
                    </Pagination>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <EmailLogDetailsDialog
        log={selectedLog}
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
        onRetry={(log) => {
          setDetailsOpen(false);
          setRetryLog(log);
          setRetryOpen(true);
        }}
      />

      <RetryEmailDialog
        log={retryLog}
        open={retryOpen}
        onOpenChange={setRetryOpen}
        onSuccess={() => refetch()}
      />
    </div>
  );
}
